/**
 * Migration Script: Legacy Areas to Location-Based Hierarchy
 * 
 * Migrates listAreas2 data to the new Location table structure
 * - Creates Location records
 * - Creates LocationLaborTargets for theoretical hours
 * - Creates ManagerOrgAssignments for managers
 * - Creates LocationAssignmentHistory for audit trail
 */

import { API, graphqlOperation } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { getStoreLocation } from './storeLocationMapping';
import { getToastSystemName } from './toastNameMapping';

// Import GraphQL operations
import {
  listAreas2,
  listLocations,
  listOrgUnits,
  getOrgUnit,
  listLocationLaborTargets,
  listUsers,
  storesByClientSorted
} from 'graphql/queries';

import { 
  createLocation,
  updateLocation,
  createLocationLaborTargets,
  updateLocationLaborTargets,
  // createManagerAssignmentHistory, // REMOVED: ManagerAssignmentHistory removed from schema
  createOrgUnit,
  updateOrgUnit,
  createHierarchyChangeLog
} from 'graphql/mutations';

// Helper function to remove __typename fields
function removeTypename(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeTypename(item));
  }
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key !== '__typename') {
      cleaned[key] = removeTypename(value);
    }
  }
  return cleaned;
}

/**
 * Main Location Migration Class
 */
export class LocationHierarchyMigration {
  constructor(clientId = 'grm') {
    this.clientId = clientId;
    this.migrationId = uuidv4();
    this.results = {
      locations: {
        created: 0,
        updated: 0,
        withStoreRecordId: 0,  // Track locations using StoreRecord.id
        withGeneratedId: 0,    // Track locations using generated UUID
        matchingStrategies: {  // Track which strategies were used
          exact: 0,
          caseInsensitive: 0,
          manualMapping: 0,
          longNameMatch: 0,
          partialMatch: 0
        },
        errors: []
      },
      laborTargets: { created: 0, updated: 0, errors: [] },
      managers: { created: 0, errors: [] },
      history: { created: 0, errors: [] },
      orgUnits: { created: 0, updated: 0, errors: [] }
    };
    this.existingLocations = new Map();
    this.existingOrgUnits = new Map();
    this.existingLaborTargets = new Map();
    this.existingStoreRecords = new Map(); // Map storeName -> StoreRecord (backward compatibility)
    this.storeRecordsByExactName = new Map(); // For exact and case-insensitive matching
    this.storeNameMappings = new Map(); // Manual mappings for known mismatches
    this.createdLocations = new Map(); // Track newly created locations
    this.userNameToIdMap = new Map(); // Map userName to userId
    // Arrays for HierarchyChangeLog
    this.createdLocationsList = [];
    this.createdOrgUnitsList = [];
    // Track stores without StoreRecord matches
    this.unmatchedStores = [];
  }

  /**
   * Main migration orchestrator
   */
  async migrate() {
    console.log(`🚀 Starting Location-based hierarchy migration for client: ${this.clientId}`);
    console.log(`📋 Migration ID: ${this.migrationId}`);

    try {
      // Step 1: Fetch existing records
      await this.fetchExistingLocations();
      console.log(`📊 Found ${this.existingLocations.size} existing Location records`);

      await this.fetchExistingOrgUnits();
      console.log(`📊 Found ${this.existingOrgUnits.size} existing OrgUnits`);

      await this.fetchExistingLaborTargets();
      console.log(`📊 Found ${this.existingLaborTargets.size} existing LocationLaborTargets`);

      await this.fetchExistingStoreRecords();
      console.log(`📊 Found ${this.existingStoreRecords.size} existing StoreRecords for ID mapping`);

      // Step 2: Fetch legacy data
      const legacyData = await this.fetchLegacyData();
      console.log(`📊 Found ${legacyData.length} areas with stores`);

      // Step 3: Ensure OrgUnits exist for all areas
      await this.ensureOrgUnits(legacyData);

      // Step 4: Migrate stores to Location records
      await this.migrateLocations(legacyData);

      // Step 5: Create LocationLaborTargets for theoretical hours
      await this.migrateLaborTargets(legacyData);

      // Step 6: Create ManagerAssignmentHistory
      await this.migrateManagerAssignments(legacyData);

      // Change logs are now created individually for each entity during migration
      console.log('✅ All individual HierarchyChangeLog entries created');

      // Print migration summary
      console.log('\n========================================');
      console.log('📊 MIGRATION SUMMARY');
      console.log('========================================');
      console.log(`✅ Total Locations created: ${this.results.locations.created}`);
      console.log(`   - With StoreRecord.id: ${this.results.locations.withStoreRecordId} (MetricData compatible)`);
      console.log(`   - With generated UUID: ${this.results.locations.withGeneratedId} (No MetricData linkage)`);

      if (this.results.locations.withStoreRecordId > 0) {
        console.log('\n📊 StoreRecord Matching Strategies Used:');
        const strategies = this.results.locations.matchingStrategies;
        if (strategies.exact > 0) console.log(`   - Exact match: ${strategies.exact}`);
        if (strategies.caseInsensitive > 0) console.log(`   - Case-insensitive: ${strategies.caseInsensitive}`);
        if (strategies.manualMapping > 0) console.log(`   - Manual mapping: ${strategies.manualMapping}`);
        if (strategies.longNameMatch > 0) console.log(`   - LongName match: ${strategies.longNameMatch}`);
        if (strategies.partialMatch > 0) console.log(`   - Partial match: ${strategies.partialMatch}`);
      }

      if (this.results.locations.errors.length > 0) {
        console.log(`\n❌ Location errors: ${this.results.locations.errors.length}`);
      }
      console.log(`\n✅ Labor Targets created: ${this.results.laborTargets.created}`);
      console.log(`✅ Manager Assignments created: ${this.results.managers.created}`);
      console.log(`✅ OrgUnits created: ${this.results.orgUnits.created}`);

      // List all stores without StoreRecord matches
      if (this.unmatchedStores.length > 0) {
        console.log('\n⚠️ ========================================');
        console.log('⚠️ STORES WITHOUT STORERECORD MATCH (Using Generated UUID)');
        console.log('⚠️ These locations will NOT have associated MetricData:');
        console.log('⚠️ ========================================');
        this.unmatchedStores.forEach((store, index) => {
          console.log(`   ${index + 1}. ${store.shortName}`);
          console.log(`      - Long Name: ${store.longName}`);
          console.log(`      - Area: ${store.areaName}`);
          console.log(`      - New Location ID: ${store.newLocationId}`);
        });
        console.log('⚠️ ========================================');
        console.log(`⚠️ Total unmatched: ${this.unmatchedStores.length} stores`);
        console.log('⚠️ Action Required: Create StoreRecords for these stores to link MetricData');
        console.log('⚠️ ========================================');
      }

      console.log('========================================\n');

      console.log('✅ Location migration completed successfully!');
      return this.results;

    } catch (error) {
      console.error('❌ Migration failed:', error);
      throw error;
    }
  }

  /**
   * Fetch existing Location records
   */
  async fetchExistingLocations() {
    try {
      let nextToken = null;
      do {
        const result = await API.graphql(
          graphqlOperation(listLocations, { 
            filter: { clientId: { eq: this.clientId } },
            limit: 100,
            nextToken
          })
        );
        
        const locations = result.data.listLocations?.items || [];
        locations.forEach(location => {
          // Map by location name for matching
          if (location.locationName) {
            this.existingLocations.set(location.locationName, location);
          }
        });
        
        nextToken = result.data.listLocations?.nextToken;
      } while (nextToken);
      
    } catch (error) {
      console.error('Error fetching existing locations:', error);
      // If table doesn't exist yet, continue with empty map
      if (error.errors?.[0]?.message?.includes('not found')) {
        console.log('Location table not yet populated, will create all records');
      }
    }
  }

  /**
   * Fetch existing OrgUnits
   */
  async fetchExistingOrgUnits() {
    try {
      const result = await API.graphql(
        graphqlOperation(listOrgUnits, {
          filter: { clientId: { eq: this.clientId } },
          limit: 1000
        })
      );
      
      const orgUnits = result.data.listOrgUnits?.items || [];
      orgUnits.forEach(orgUnit => {
        this.existingOrgUnits.set(orgUnit.id, orgUnit);
        // Also map by name for lookup
        if (orgUnit.name) {
          this.existingOrgUnits.set(orgUnit.name.toLowerCase(), orgUnit);
        }
      });
    } catch (error) {
      console.error('Error fetching OrgUnits:', error);
    }
  }

  /**
   * Fetch existing LocationLaborTargets
   */
  async fetchExistingLaborTargets() {
    try {
      let nextToken = null;
      do {
        const result = await API.graphql(
          graphqlOperation(listLocationLaborTargets, {
            filter: { clientId: { eq: this.clientId } },
            limit: 100,
            nextToken
          })
        );
        
        const targets = result.data.listLocationLaborTargets?.items || [];
        targets.forEach(target => {
          // Map by locationId for matching
          if (target.locationId) {
            this.existingLaborTargets.set(target.locationId, target);
          }
        });
        
        nextToken = result.data.listLocationLaborTargets?.nextToken;
      } while (nextToken);
      
    } catch (error) {
      console.error('Error fetching labor targets:', error);
      // If table doesn't exist yet, continue with empty map
    }
  }

  /**
   * Fetch existing StoreRecords and create multiple lookup maps for flexible matching
   */
  async fetchExistingStoreRecords() {
    console.log('📥 Fetching StoreRecords for ID mapping...');
    try {
      let nextToken = null;
      let storeCount = 0;

      // Initialize multiple lookup strategies (only if not already initialized)
      if (!this.storeRecordsByExactName) {
        this.storeRecordsByExactName = new Map();  // Exact storeName match
      }

      // Clear the map before populating
      this.storeRecordsByExactName.clear();

      // Initialize manual mappings
      this.storeNameMappings = new Map([
        // Manual mappings for known mismatches (shortName -> StoreRecord.storeName)
        ["Fort Worth", "Clearfork"],
        ["Irving", "Las Colinas"],  // If Irving in listAreas2 maps to Las Colinas in StoreRecord
        ["WW DC Ranch", "DC Ranch"],  // Remove prefix
        ["WS West Shore", "Westshore"],  // Remove prefix and adjust spacing
        ["DATG Palm Beach", "DATG"],  // Simplify name
        // Add other known mappings as discovered
      ]);

      do {
        console.log(`   Fetching StoreRecords batch (nextToken: ${nextToken ? 'yes' : 'no'})...`);
        const result = await API.graphql(
          graphqlOperation(storesByClientSorted, {
            clientId: this.clientId,
            limit: 100,
            nextToken
          })
        );

        const storeRecords = result.data.storesByClientSorted?.items || [];
        console.log(`   Received ${storeRecords.length} StoreRecords in this batch`);
        console.log('storeRecords', storeRecords)
        // Check if our problem stores are in this batch
        const problemStores = ['South Rainbow', 'Fritz Farm', 'Old Town', 'Prairiefire'];
        const foundInBatch = storeRecords.filter(s => problemStores.includes(s.storeName));
        if (foundInBatch.length > 0) {
          console.log(`   📍 Found problem stores in this batch:`, foundInBatch.map(s => s.storeName));
        }

        storeRecords.forEach(store => {
          if (store.storeName && store.id) {
            // Trim any whitespace from storeName
            const trimmedName = store.storeName.trim();

            // Debug log for problem stores
            if (problemStores.includes(trimmedName)) {
              console.log(`   📍 Adding to map: "${trimmedName}" with ID: ${store.id}`);
            }

            // Store exact name (case-sensitive)
            this.storeRecordsByExactName.set(trimmedName, store);
            // Also store lowercase version for case-insensitive matching
            this.storeRecordsByExactName.set(trimmedName.toLowerCase(), store);

            // Keep the original map for backward compatibility
            this.existingStoreRecords.set(trimmedName, store);
            storeCount++;
          }
        });

        nextToken = result.data.storesByClientSorted?.nextToken;
      } while (nextToken);

      console.log(`✅ Loaded ${storeCount} StoreRecords for ID mapping`);
      console.log(`   Manual mappings configured: ${this.storeNameMappings.size}`);

      // Debug: Show ALL store names that were loaded
      const allNames = Array.from(this.existingStoreRecords.keys());
      console.log(`📋 ALL ${allNames.length} StoreRecords loaded:`);
      allNames.forEach(name => {
        console.log(`   - "${name}"`);
      });

      // Check for specific stores that were reported as not matching
      const checkStores = ['South Rainbow', 'Fritz Farm', 'Old Town', 'Prairiefire'];
      console.log('\n🔍 Checking for specific stores in loaded data:');
      checkStores.forEach(name => {
        const found = this.storeRecordsByExactName.has(name) || this.storeRecordsByExactName.has(name.toLowerCase());
        const record = this.storeRecordsByExactName.get(name) || this.storeRecordsByExactName.get(name.toLowerCase());
        if (found) {
          console.log(`   - "${name}": ✅ Found with ID: ${record.id}`);
        } else {
          console.log(`   - "${name}": ❌ NOT FOUND in loaded StoreRecords`);
          // Check if it's there with different casing or spacing
          const similar = allNames.filter(n => n.toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(n.toLowerCase()));
          if (similar.length > 0) {
            console.log(`     Possible matches: ${similar.join(', ')}`);
          }
        }
      });

    } catch (error) {
      console.error('Error fetching StoreRecords:', error);
      // Initialize empty maps if fetch fails
      this.storeRecordsByExactName = new Map();
      this.storeNameMappings = new Map();
      console.warn('⚠️ Could not fetch StoreRecords. Will use generated UUIDs for all Locations.');
    }
  }

  /**
   * Fetch all users and create userName to userId mapping
   */
  async fetchUserMapping() {
    console.log('📥 Fetching user data for manager mapping...');
    try {
      let nextToken = null;
      let userCount = 0;
      
      do {
        const result = await API.graphql(
          graphqlOperation(listUsers, {
            filter: { client: { eq: this.clientId } },
            limit: 100,
            nextToken
          })
        );
        
        const users = result.data.listUsers?.items || [];
        users.forEach(user => {
          if (user.userName && user.id) {
            this.userNameToIdMap.set(user.userName, user.id);
            userCount++;
          }
        });
        
        nextToken = result.data.listUsers?.nextToken;
      } while (nextToken);
      
      console.log(`✅ Loaded ${userCount} users for manager mapping`);
      console.log(`   Sample mappings:`, Array.from(this.userNameToIdMap.entries()).slice(0, 3));
      
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error('Failed to fetch users for manager mapping');
    }
  }

  /**
   * Fetch legacy data from listAreas2
   */
  async fetchLegacyData() {
    const result = await API.graphql(
      graphqlOperation(listAreas2, {
        report: 'store-admin',
        role: 'Admin',
        user: 'grmAdmin'
      })
    );

    return result.data?.listAreas2 || [];
  }

  /**
   * Find StoreRecord using multiple matching strategies
   * @param {string} shortName - The store's shortName from listAreas2
   * @param {string} longName - The store's longName from listAreas2
   * @returns {object|null} The matching StoreRecord or null
   */
  findStoreRecord(shortName, longName) {
    // Trim any whitespace from inputs
    const trimmedShortName = shortName ? shortName.trim() : shortName;
    const trimmedLongName = longName ? longName.trim() : longName;

    // Only log for stores we're debugging
    const debugStores = ['South Rainbow', 'Fritz Farm', 'Old Town', 'Prairiefire'];
    if (debugStores.includes(trimmedShortName)) {
      console.log(`🔍 DEBUG - Searching for StoreRecord: shortName="${trimmedShortName}", longName="${trimmedLongName || 'N/A'}"`);
      console.log(`   Map has key "${trimmedShortName}": ${this.storeRecordsByExactName.has(trimmedShortName)}`);
      console.log(`   Map has lowercase key "${trimmedShortName.toLowerCase()}": ${this.storeRecordsByExactName.has(trimmedShortName.toLowerCase())}`);
    }

    // Strategy 1: Try exact match with shortName
    let storeRecord = this.storeRecordsByExactName.get(trimmedShortName);
    if (storeRecord) {
      console.log(`✅ Found StoreRecord via exact match: "${trimmedShortName}"`);
      this.results.locations.matchingStrategies.exact++;
      return storeRecord;
    }

    // Strategy 2: Try case-insensitive match
    storeRecord = this.storeRecordsByExactName.get(trimmedShortName.toLowerCase());
    if (storeRecord) {
      console.log(`✅ Found StoreRecord via case-insensitive match: "${trimmedShortName}"`);
      this.results.locations.matchingStrategies.caseInsensitive++;
      return storeRecord;
    }

    // Strategy 3: Try manual mapping
    const mappedName = this.storeNameMappings.get(trimmedShortName);
    if (mappedName) {
      storeRecord = this.storeRecordsByExactName.get(mappedName);
      if (storeRecord) {
        console.log(`✅ Found StoreRecord via manual mapping: "${trimmedShortName}" → "${mappedName}"`);
        this.results.locations.matchingStrategies.manualMapping++;
        return storeRecord;
      }
    }

    // Strategy 4: Check if any StoreRecord name appears in the longName
    if (trimmedLongName) {
      // Clean up longName (remove extra spaces, hyphens)
      const cleanLongName = trimmedLongName.replace(/\s*-\s*/g, ' ').toLowerCase();

      for (const [storeName, record] of this.storeRecordsByExactName) {
        // Skip lowercase duplicates
        if (storeName === storeName.toLowerCase() && this.storeRecordsByExactName.has(storeName.charAt(0).toUpperCase() + storeName.slice(1))) {
          continue;
        }

        // Check if storeName appears in longName
        if (storeName && cleanLongName.includes(storeName.toLowerCase())) {
          console.log(`✅ Found StoreRecord via longName match: "${storeName}" found in "${trimmedLongName}"`);
          this.results.locations.matchingStrategies.longNameMatch++;
          return record;
        }
      }
    }

    // Strategy 5: Try partial matches as last resort (less reliable)
    // Only use this for stores with distinctive names
    if (trimmedShortName && trimmedShortName.length > 4) {  // Avoid false matches with short names
      for (const [storeName, record] of this.storeRecordsByExactName) {
        // Skip lowercase duplicates
        if (storeName === storeName.toLowerCase()) continue;

        // Check bidirectional partial matches
        if ((trimmedShortName.toLowerCase().includes(storeName.toLowerCase()) ||
             storeName.toLowerCase().includes(trimmedShortName.toLowerCase())) &&
            storeName !== trimmedShortName) {  // Avoid re-matching exact matches
          console.warn(`⚠️ Found potential StoreRecord via partial match: "${storeName}" ~ "${trimmedShortName}" (less reliable)`);
          this.results.locations.matchingStrategies.partialMatch++;
          return record;
        }
      }
    }

    return null;
  }

  /**
   * Ensure OrgUnits exist for all areas
   */
  async ensureOrgUnits(legacyAreas) {
    console.log('📦 Ensuring OrgUnits exist for all areas...');
    
    for (const area of legacyAreas) {
      try {
        // Check if OrgUnit exists by ID or name
        let orgUnit = this.existingOrgUnits.get(area.areaId) || 
                     this.existingOrgUnits.get(area.areaName?.toLowerCase());
        
        if (!orgUnit) {
          // Create new OrgUnit
          console.log(`Creating OrgUnit for area: ${area.areaName}`);
          
          const input = {
            id: area.areaId,
            clientId: this.clientId,
            name: area.areaName,
            orgLevel: 'ORGLEVEL_1',
            isActive: true,
            sortOrder: 0,
            metadata: JSON.stringify({
              source: 'listAreas2',
              migrationDate: new Date().toISOString()
            })
          };
          
          const result = await API.graphql(
            graphqlOperation(createOrgUnit, { input })
          );
          
          orgUnit = result.data.createOrgUnit;
          this.existingOrgUnits.set(orgUnit.id, orgUnit);
          this.existingOrgUnits.set(orgUnit.name.toLowerCase(), orgUnit);
          this.results.orgUnits.created++;
          
          console.log(`✅ Created OrgUnit: ${orgUnit.name} (${orgUnit.id})`);
          
          // Create audit log entry
          await this.createHierarchyChangeEntry(
            'OrgUnit',
            orgUnit.id,
            orgUnit.name,
            'CREATION',
            {
              migrationId: this.migrationId,
              source: 'listAreas2',
              areaType: area.areaType,
              storeCount: area.stores.length
            }
          );
          
          // Track created OrgUnit for change log
          this.createdOrgUnitsList.push({ 
            id: orgUnit.id, 
            name: orgUnit.name 
          });
        } else {
          console.log(`✓ OrgUnit already exists: ${orgUnit.name}`);
        }
      } catch (error) {
        console.error(`Failed to ensure OrgUnit for ${area.areaName}:`, error);
        this.results.orgUnits.errors.push({
          area: area.areaName,
          error: error.message
        });
      }
    }
  }

  /**
   * Migrate stores to Location records
   */
  async migrateLocations(legacyAreas) {
    console.log('📦 Migrating stores to Location records...');

    for (const area of legacyAreas) {
      for (const store of area.stores) {
        try {
          // Check if Location already exists
          let location = this.existingLocations.get(store.shortName);

          if (!location) {
            // Look up StoreRecord using multiple matching strategies
            const storeRecord = this.findStoreRecord(store.shortName, store.longName);
            let locationId;

            if (storeRecord) {
              locationId = storeRecord.id;
              console.log(`✅ Using StoreRecord.id for ${store.shortName}: ${locationId}`);
              this.results.locations.withStoreRecordId++;
            } else {
              locationId = uuidv4();
              console.warn(`⚠️ WARNING: No StoreRecord found for store: ${store.shortName} (longName: ${store.longName || 'N/A'}). Creating Location with new UUID: ${locationId}. MetricData will NOT map to this Location.`);
              this.results.locations.withGeneratedId++;
              // Track unmatched store details
              this.unmatchedStores.push({
                shortName: store.shortName,
                longName: store.longName || 'N/A',
                areaName: area.areaName,
                newLocationId: locationId
              });
            }

            // Create new Location
            console.log(`Creating Location for: ${store.shortName}`);

            const locationInput = this.mapLegacyStoreToLocation(store, area, locationId);

            // Debug: Log the exact input being sent
            console.log('Location input:', JSON.stringify(locationInput, null, 2));

            const result = await API.graphql(
              graphqlOperation(createLocation, { input: locationInput })
            );

            location = result.data.createLocation;
            this.existingLocations.set(location.locationName, location);
            this.createdLocations.set(location.id, location);
            this.results.locations.created++;
            
            console.log(`✅ Created Location: ${location.locationName} (${location.id})`);
            
            // Create audit log entry
            await this.createHierarchyChangeEntry(
              'Location',
              location.id,
              location.locationName,
              'CREATION',
              {
                migrationId: this.migrationId,
                source: 'listAreas2',
                legacyStoreId: store.storeId,
                areaId: area.areaId
              }
            );
            
            // Track created location for change log
            this.createdLocationsList.push({ 
              id: location.id, 
              name: location.locationName 
            });
          } else {
            console.log(`✓ Location already exists: ${location.locationName}`);
            // Optionally update if needed
            // await this.updateLocationIfNeeded(location, store, area);
          }
        } catch (error) {
          console.error(`❌ Failed to migrate location ${store.shortName}:`, error);
          
          // Log detailed error information
          if (error.errors) {
            console.error('GraphQL errors:', JSON.stringify(error.errors, null, 2));
          }
          if (error.data) {
            console.error('Error data:', error.data);
          }
          
          this.results.locations.errors.push({
            store: store.shortName,
            error: error.message || JSON.stringify(error)
          });
          
          // EXIT ON FIRST FAILURE
          console.error('🛑 Stopping location migration due to failure');
          throw new Error(`Location migration failed for ${store.shortName}: ${error.message}`);
        }
      }
    }
  }

  /**
   * Map legacy store data to Location format
   */
  mapLegacyStoreToLocation(legacyStore, area, locationId) {
    const storeLocation = getStoreLocation(legacyStore.shortName);

    // Get Toast system name, log warning if missing
    const toastSystemName = getToastSystemName(legacyStore.shortName);
    if (!toastSystemName) {
      console.warn(`⚠️ WARNING: No Toast system name mapping found for store: ${legacyStore.shortName}. Setting systemName to null.`);
    }

    // Check if timezone exists, log warning if missing
    if (!storeLocation?.timeZone) {
      console.warn(`⚠️ WARNING: No timezone mapping found for store: ${legacyStore.shortName}. Setting timezone to "No Time Zone Defined".`);
    }

    return {
      id: locationId,  // Use provided locationId (either StoreRecord.id or generated UUID)
      clientId: this.clientId,
      locationName: legacyStore.shortName,
      locationNumber: null, // Always null for all locations

      // Both TOAST and ALOHA external systems
      externalSystems: [
        {
          systemType: 'TOAST_POS',
          systemId: legacyStore.toastId || '',
          systemName: toastSystemName || null,  // Set to null if not found
          isActive: true,
          isPrimaryPOS: true,
          lastSyncAt: null,
          syncStatus: 'ACTIVE',
          metadata: null
        },
        {
          systemType: 'ALOHA_POS',
          systemId: legacyStore.shortName,  // Use shortName for systemId
          systemName: legacyStore.longName || null,  // Use longName for systemName
          isActive: false,
          isPrimaryPOS: false,
          lastSyncAt: null,
          syncStatus: 'DISABLED',
          metadata: null
        }
      ],

      orgLevel_1: area.areaId,  // OrgUnit ID only
      orgLevel_2: null,  // Not used in current hierarchy
      orgLevel_3: null,  // Not used in current hierarchy

      ownershipType: area.areaType || 'Company',  // From area, not store
      conceptId: null,

      timeZone: storeLocation?.timeZone || 'No Time Zone Defined',  // Use specific string if not found
      address: this.mapToLocationAddress(storeLocation),

      // openDate and closeDate are AWSDate type (YYYY-MM-DD), not included in migration
      // Note: Location doesn't have isActive field - store status in metadata

      metadata: JSON.stringify({
        legacyStoreId: legacyStore.storeId,
        thresholds: removeTypename(legacyStore.thresholds),  // Remove __typename from threshold data
        isActive: legacyStore.status === 'active' || legacyStore.status === 'Active',
        migrationDate: new Date().toISOString()
        // Removed migrationId and source fields as requested
      })

      // Note: Removed lastHierarchyChange and lastHierarchyChangeBy - might be auto-generated or restricted
    };
  }

  /**
   * Map store location to LocationAddress format
   */
  mapToLocationAddress(storeLocation) {
    if (!storeLocation?.address) return null;
    
    const addr = storeLocation.address;
    return {
      street_1: addr.street1 || '',
      street_2: addr.street2 || null,
      city: addr.city || '',
      state: addr.state || '',
      zipCode: addr.zip || '',
      country: addr.country || 'US'
    };
  }

  /**
   * Migrate theoretical hours to LocationLaborTargets
   */
  async migrateLaborTargets(legacyAreas) {
    console.log('📦 Creating LocationLaborTargets for theoretical hours...');
    
    for (const area of legacyAreas) {
      for (const store of area.stores) {
        try {
          // Find the Location record we created
          const location = this.existingLocations.get(store.shortName);
          
          if (!location) {
            console.warn(`⚠️ No Location found for ${store.shortName}, skipping labor targets`);
            continue;
          }
          
          // Check if labor targets already exist for this location
          const existingTarget = this.existingLaborTargets.get(location.id);
          
          if (!existingTarget) {
            console.log(`Creating labor targets for: ${store.shortName}`);
            
            const targetInput = this.createLaborTargetsFromStore(store, location.id);
            
            // Validate required fields
            if (!targetInput.effectiveDate) {
              console.error(`❌ Missing effectiveDate for ${store.shortName}`);
              targetInput.effectiveDate = new Date().toISOString();
            }
            
            // Debug log the input
            console.log('Labor target input:', JSON.stringify(targetInput, null, 2));
            
            const result = await API.graphql(
              graphqlOperation(createLocationLaborTargets, { input: targetInput })
            );
            
            const target = result.data.createLocationLaborTargets;
            this.existingLaborTargets.set(location.id, target);
            this.results.laborTargets.created++;
            
            console.log(`✅ Created labor targets for: ${store.shortName}`);
            
            // Create individual change log entry for this Labor Target
            await this.createHierarchyChangeEntry(
              'LocationLaborTargets',
              target.id,
              store.shortName,
              'CREATION',
              {
                migrationId: this.migrationId,
                source: 'listAreas2',
                locationId: location.id,
                fohBaseHours: targetInput.fohBaseHours,
                bohBaseHours: targetInput.bohBaseHours
              }
            );
          } else {
            console.log(`✓ Labor targets already exist for: ${store.shortName}`);
          }
        } catch (error) {
          console.error(`❌ Failed to create labor targets for ${store.shortName}:`, error);
          
          // Log detailed error information
          if (error.errors) {
            console.error('GraphQL errors:', JSON.stringify(error.errors, null, 2));
          }
          
          this.results.laborTargets.errors.push({
            store: store.shortName,
            error: error.message || JSON.stringify(error)
          });
          
          // EXIT ON FIRST FAILURE
          console.error('🛑 Stopping labor targets migration due to failure');
          throw new Error(`Labor targets migration failed for ${store.shortName}: ${error.message}`);
        }
      }
    }
  }

  /**
   * Create LocationLaborTargets from store data
   */
  createLaborTargetsFromStore(legacyStore, locationId) {
    // Convert theoretical hours to daily format
    const convertToDaily = (theoHrs) => {
      if (!theoHrs) {
        return {
          Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0
        };
      }
      
      // Remove __typename if present
      const cleanTheoHrs = removeTypename(theoHrs);
      
      // If it's already in daily format
      if (cleanTheoHrs.Mon !== undefined) {
        return {
          Mon: cleanTheoHrs.Mon || 0,
          Tue: cleanTheoHrs.Tue || 0,
          Wed: cleanTheoHrs.Wed || 0,
          Thu: cleanTheoHrs.Thu || 0,
          Fri: cleanTheoHrs.Fri || 0,
          Sat: cleanTheoHrs.Sat || 0,
          Sun: cleanTheoHrs.Sun || 0
        };
      }
      
      // If it's in weekday/weekend format
      if (cleanTheoHrs.weekday !== undefined || cleanTheoHrs.weekend !== undefined) {
        const weekdayHours = cleanTheoHrs.weekday || 0;
        const weekendHours = cleanTheoHrs.weekend || 0;
        
        return {
          Mon: weekdayHours,
          Tue: weekdayHours,
          Wed: weekdayHours,
          Thu: weekdayHours,
          Fri: weekdayHours,
          Sat: weekendHours,
          Sun: weekendHours
        };
      }
      
      // Default if no recognizable format
      return {
        Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0
      };
    };
    
    return {
      clientId: this.clientId,
      locationId: locationId,
      
      fohBaseHours: convertToDaily(legacyStore.fohBaseTheoHrs),
      bohBaseHours: convertToDaily(legacyStore.bohBaseTheoHrs),
      
      // SPLH targets - updated per requirements
      fohSplhTargets: {
        Mon: 316, Tue: 316, Wed: 316, Thu: 316, 
        Fri: 230, Sat: 230, Sun: 230
      },
      bohSplhTargets: {
        Mon: 345, Tue: 345, Wed: 345, Thu: 345,
        Fri: 316, Sat: 316, Sun: 316
      },
      
      effectiveDate: new Date().toISOString(),
      // endDate: null, // Don't include null fields
      isActive: true,
      
      reasonType: 'INITIAL_SETUP',
      reason: 'Initial migration from listAreas2',
      
      // Don't include null fields for supersedes/supersededBy
      // supersedes: null,
      // supersededBy: null,
      
      changedBy: 'migration-script',
      
      // Required timestamp fields
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };
  }

  /**
   * Migrate manager assignments to ManagerOrgAssignment
   */
  async migrateManagerAssignments(legacyAreas) {
    console.log('📦 Creating ManagerOrgAssignments...');
    
    // First, fetch all users and create userName -> userId mapping
    await this.fetchUserMapping();
    
    let areaManagerCount = 0;
    let storeManagerCount = 0;
    let skippedStores = 0;
    let skippedManagers = 0;
    let failedDueToError = false;
    
    for (const area of legacyAreas) {
      // Process area managers
      if (area.areaManager && area.areaManager.trim()) {
        console.log(`\n📋 Processing Area Manager for ${area.areaName}...`);
        console.log(`  Manager userName: "${area.areaManager}"`);
        
        // Map userName to userId
        const userId = this.userNameToIdMap.get(area.areaManager);
        if (!userId) {
          console.warn(`  ⚠️ No user found with userName: "${area.areaManager}" - skipping`);
          skippedManagers++;
          this.results.managers.errors.push({
            area: area.areaName,
            error: `User not found: ${area.areaManager}`
          });
          continue;
        }
        
        console.log(`  Mapped to userId: "${userId}"`);
        
        const result = await this.createManagerAssignment(
          userId,
          area.areaId,
          'ORGLEVEL_1',
          `Area Manager for ${area.areaName}`,
          area.areaName  // Pass area name for snapshot
        );
        
        if (result.success) {
          areaManagerCount++;
        } else if (result.exitOnError) {
          console.error('\n❌ CRITICAL ERROR - Stopping manager migration');
          console.error(`  Area: ${area.areaName}`);
          console.error(`  Manager userName: "${area.areaManager}"`);
          console.error(`  Mapped userId: "${userId}"`);
          console.error(`  Error: ${result.error}`);
          failedDueToError = true;
          break;
        }
      }
      
      if (failedDueToError) break;
      
      // Process store managers (GMs)
      console.log(`\n📋 Processing Store Managers for ${area.areaName}...`);
      for (const store of area.stores) {
        if (store.gm && Array.isArray(store.gm) && store.gm.length > 0) {
          const location = this.existingLocations.get(store.shortName);
          
          if (location) {
            console.log(`  Processing ${store.gm.length} manager(s) for store: ${store.shortName}`);
            for (const gmUserName of store.gm) {
              // Skip empty or null manager userNames
              if (!gmUserName || !gmUserName.trim()) {
                console.warn(`    ⚠️ Skipping empty/null manager userName for store ${store.shortName}`);
                continue;
              }
              
              console.log(`    Manager userName: "${gmUserName}"`);
              
              // Map userName to userId
              const userId = this.userNameToIdMap.get(gmUserName);
              if (!userId) {
                console.warn(`    ⚠️ No user found with userName: "${gmUserName}" - skipping`);
                skippedManagers++;
                this.results.managers.errors.push({
                  store: store.shortName,
                  error: `User not found: ${gmUserName}`
                });
                continue;
              }
              
              console.log(`    Mapped to userId: "${userId}"`);
              
              const result = await this.createManagerAssignment(
                userId,
                location.id,
                'LOCATION_LEVEL',
                `Store Manager for ${store.shortName}`,
                store.shortName  // Pass store name for snapshot
              );
              
              if (result.success) {
                storeManagerCount++;
              } else if (result.exitOnError) {
                console.error('\n❌ CRITICAL ERROR - Stopping manager migration');
                console.error(`  Store: ${store.shortName}`);
                console.error(`  Manager userName: "${gmUserName}"`);
                console.error(`  Mapped userId: "${userId}"`);
                console.error(`  Location ID: ${location.id}`);
                console.error(`  Error: ${result.error}`);
                failedDueToError = true;
                break;
              }
            }
            if (failedDueToError) break;
          } else {
            console.warn(`  ⚠️ No Location found for store ${store.shortName} - skipping manager assignments`);
            skippedStores++;
            this.results.managers.errors.push({
              store: store.shortName,
              error: 'Location not found - cannot create manager assignment'
            });
          }
        }
        if (failedDueToError) break;
      }
      if (failedDueToError) break;
    }
    
    console.log('\n📊 Manager Assignment Summary:');
    console.log(`  ✅ Area Managers created: ${areaManagerCount}`);
    console.log(`  ✅ Store Managers created: ${storeManagerCount}`);
    console.log(`  ✅ Total assignments: ${this.results.managers.created}`);
    if (skippedManagers > 0) {
      console.log(`  ⚠️ Managers skipped (user not found): ${skippedManagers}`);
    }
    if (skippedStores > 0) {
      console.log(`  ⚠️ Stores skipped (no Location found): ${skippedStores}`);
    }
    if (this.results.managers.errors.length > 0) {
      console.log(`  ❌ Failed assignments: ${this.results.managers.errors.length}`);
    }
    if (failedDueToError) {
      console.log('\n⛔ Migration stopped due to critical error - check logs above');
    }
  }

  /**
   * Create a single ManagerOrgAssignment
   */
  async createManagerAssignment(userId, orgUnitId, managementLevel, reason, orgUnitName = null) {
    // Validate required fields
    if (!userId || typeof userId !== 'string' || !userId.trim()) {
      const errorMsg = `Invalid userId: value is ${userId === null ? 'null' : userId === undefined ? 'undefined' : `empty string or invalid type: "${userId}"`}`;
      console.error(`❌ Validation Error: ${errorMsg}`);
      this.results.managers.errors.push({
        userId: userId || 'null/undefined',
        orgUnitId,
        managementLevel,
        error: errorMsg
      });
      return { success: false, exitOnError: true, error: errorMsg };
    }
    
    if (!orgUnitId || typeof orgUnitId !== 'string' || !orgUnitId.trim()) {
      const errorMsg = `Invalid orgUnitId: value is ${orgUnitId === null ? 'null' : orgUnitId === undefined ? 'undefined' : `empty string or invalid type: "${orgUnitId}"`}`;
      console.error(`❌ Validation Error: ${errorMsg}`);
      this.results.managers.errors.push({
        userId,
        orgUnitId: orgUnitId || 'null/undefined',
        managementLevel,
        error: errorMsg
      });
      return { success: false, exitOnError: true, error: errorMsg };
    }
    
    if (!this.clientId || typeof this.clientId !== 'string' || !this.clientId.trim()) {
      const errorMsg = `Invalid clientId: value is ${this.clientId === null ? 'null' : this.clientId === undefined ? 'undefined' : `empty string or invalid type: "${this.clientId}"`}`;
      console.error(`❌ Validation Error: ${errorMsg}`);
      this.results.managers.errors.push({
        userId,
        orgUnitId,
        managementLevel,
        error: errorMsg
      });
      return { success: false, exitOnError: true, error: errorMsg };
    }
    
    try {
      // Determine the org unit name snapshot
      let orgUnitNameSnapshot = orgUnitName || 'Unknown';
      if (managementLevel === 'LOCATION_LEVEL') {
        // For stores, look up the location name
        const location = Array.from(this.existingLocations.values()).find(loc => loc.id === orgUnitId);
        if (location) {
          orgUnitNameSnapshot = location.locationName;
        }
      } else if (managementLevel === 'ORGLEVEL_1') {
        // For areas, look up the OrgUnit name
        const orgUnit = this.existingOrgUnits.get(orgUnitId);
        if (orgUnit) {
          orgUnitNameSnapshot = orgUnit.name;
        }
      }
      
      const input = {
        clientId: this.clientId.trim(),
        userId: userId.trim(),
        managementLevel: managementLevel,
        orgUnitId: orgUnitId.trim(),
        isActive: true,
        effectiveDate: new Date().toISOString(),
        endDate: null,
        assignedBy: 'migration-script', // This should be an ID, using migration-script as placeholder
        assignmentReason: reason,
        // Required fields that were missing:
        managerNameSnapshot: userId.trim(), // Using userId as placeholder for manager name
        orgUnitNameSnapshot: orgUnitNameSnapshot,
        lastModified: new Date().toISOString()
        // Note: changedBy is optional so we can omit it
      };
      
      // Log the ENTIRE input object for debugging
      console.log(`    Full input object being sent to createManagerAssignmentHistory:`);
      console.log(JSON.stringify(input, null, 2));
      
      // REMOVED: createManagerAssignmentHistory no longer exists
      // await API.graphql(
      //   graphqlOperation(createManagerAssignmentHistory, { input })
      // );
      
      this.results.managers.created++;
      console.log(`    ✅ Created manager assignment: ${userId} -> ${managementLevel}`);
      
      // Create audit log entry for manager assignment
      await this.createHierarchyChangeEntry(
        'ManagerAssignment',
        `${userId}_${orgUnitId}`,
        `${userId} -> ${orgUnitNameSnapshot}`,
        'ASSIGNMENT',
        {
          migrationId: this.migrationId,
          source: 'listAreas2',
          managementLevel: managementLevel,
          userId: userId,
          orgUnitId: orgUnitId
        }
      );
      
      return { success: true };
      
    } catch (error) {
      // Check if assignment already exists
      if (error.errors?.[0]?.message?.includes('duplicate') || 
          error.errors?.[0]?.message?.includes('already exists')) {
        console.log(`    ✓ Manager assignment already exists: ${userId}`);
        return { success: true };
      } else {
        // This is a critical error - we should stop
        const errorMsg = error.errors?.[0]?.message || error.message || JSON.stringify(error);
        console.error(`    ❌ Failed to create manager assignment for ${userId}:`, errorMsg);
        this.results.managers.errors.push({
          userId,
          orgUnitId,
          managementLevel,
          error: errorMsg
        });
        return { success: false, exitOnError: true, error: errorMsg };
      }
    }
  }

  /**
   * @deprecated - Now creating individual change log entries for each entity
   * Keeping for reference but not used
   */
  async createMigrationChangeLogBulk() {
    try {
      console.log('📝 Creating HierarchyChangeLog for migration...');
      
      // Build changeDetails array
      const changeDetails = [];
      
      // Add location details
      for (const location of this.createdLocationsList) {
        changeDetails.push({
          entityType: 'Location',
          entityId: location.id,
          entityName: location.name,
          operationType: 'CREATION',
          fieldChanges: [{
            fieldName: 'all fields',
            oldValue: null,
            newValue: 'created',
            oldDisplayValue: null,
            newDisplayValue: 'All fields created during migration'
          }]
        });
      }
      
      // Add OrgUnit details
      for (const orgUnit of this.createdOrgUnitsList) {
        changeDetails.push({
          entityType: 'OrgUnit',
          entityId: orgUnit.id,
          entityName: orgUnit.name,
          operationType: 'CREATION',
          fieldChanges: [{
            fieldName: 'all fields',
            oldValue: null,
            newValue: 'created',
            oldDisplayValue: null,
            newDisplayValue: 'All fields created during migration'
          }]
        });
      }
      
      const input = {
        clientId: this.clientId,
        changeType: 'BULK_LOCATION_MIGRATION',
        affectedLocations: this.createdLocationsList.map(l => l.id),
        affectedOrgUnits: this.createdOrgUnitsList.map(o => o.id),
        changeDetails: JSON.stringify(changeDetails), // Store as JSON string
        changeSummary: `Location migration from MongoDB - Created ${this.createdLocationsList.length} locations and ${this.createdOrgUnitsList.length} OrgUnits`,
        changeReason: 'Consolidate resources to AWS',
        changedBy: 'migration-script',
        changedByName: 'Migration Script',
        changedAt: new Date().toISOString()
      };
      
      await API.graphql(
        graphqlOperation(createHierarchyChangeLog, { input })
      );
      
      this.results.history.created++;
      console.log(`✅ Created HierarchyChangeLog for migration with ${this.createdLocationsList.length} locations and ${this.createdOrgUnitsList.length} OrgUnits`);
      
    } catch (error) {
      console.error('Failed to create HierarchyChangeLog:', error);
      this.results.history.errors.push({
        type: 'HierarchyChangeLog',
        error: error.message
      });
    }
  }


  /**
   * Create HierarchyChangeLog entry for audit trail
   */
  async createHierarchyChangeEntry(entityType, entityId, entityName, operationType, metadata) {
    // Build the changeDetails array with a single ChangeDetail object
    const changeDetail = {
      entityType: entityType,
      entityId: entityId,
      entityName: entityName || 'Unknown',
      operationType: operationType || 'CREATION',
      fieldChanges: [{
        fieldName: 'all fields',
        oldValue: null,
        newValue: 'created',
        oldDisplayValue: null,
        newDisplayValue: `Created during migration from listAreas2`
      }],
      metadata: metadata ? JSON.stringify(metadata) : null
    };
    
    // Determine affected entities based on entity type
    let affectedLocations = [];
    let affectedOrgUnits = [];
    
    if (entityType === 'Location') {
      affectedLocations = [entityId];
    } else if (entityType === 'OrgUnit') {
      affectedOrgUnits = [entityId];
    } else if (entityType === 'LocationLaborTargets' && metadata?.locationId) {
      // For labor targets, the affected location is the parent location
      affectedLocations = [metadata.locationId];
    } else if (entityType === 'ManagerAssignment' && metadata?.orgUnitId) {
      // For manager assignments, check if it's for a location or OrgUnit
      if (metadata.managementLevel === 'LOCATION_LEVEL') {
        affectedLocations = [metadata.orgUnitId];
      } else {
        affectedOrgUnits = [metadata.orgUnitId];
      }
    }
    
    const input = {
      clientId: this.clientId,
      changeType: `${entityType.toUpperCase()}_MIGRATION`,
      affectedLocations: affectedLocations,
      affectedOrgUnits: affectedOrgUnits,
      changeDetails: [changeDetail], // Array of ChangeDetail objects
      changeSummary: `Migrated ${entityType}: ${entityName}`,
      changeReason: 'Initial migration from listAreas2',
      changedBy: 'migration-script',
      changedByName: 'Migration Script',
      changedAt: new Date().toISOString()
    };

    try {
      await API.graphql(
        graphqlOperation(createHierarchyChangeLog, { input })
      );
      
      console.log(`📝 Created audit log for ${entityType}: ${entityName}`);
      this.results.history.created++;
    } catch (error) {
      // Better error logging
      const errorMessage = error.errors?.[0]?.message || error.message || JSON.stringify(error);
      console.warn(`⚠️ Failed to create audit log for ${entityType} ${entityId}:`, errorMessage);
      
      // Log the input that failed for debugging
      if (error.errors?.[0]?.message) {
        console.warn('Failed input:', JSON.stringify(input, null, 2));
      }
      
      this.results.history.errors.push({
        entityType: entityType,
        entityId: entityId,
        error: errorMessage
      });
      // Don't fail the migration if audit log fails
    }
  }

}

/**
 * Main migration function
 */
export async function runLocationMigration(clientId = 'grm') {
  const migration = new LocationHierarchyMigration(clientId);
  return await migration.migrate();
}